import * as React from 'react';
import * as DialogPrimitive from '@radix-ui/react-dialog';
import { X } from 'lucide-react';
import { cva } from 'class-variance-authority';

import { cn } from '../../utils/cn';

const dialogCloseVariants = cva('rocco-dialog-close', {
    variants: {
        variant: {
            default:
                'absolute top-4 right-4 rounded-xs opacity-70 ring-offset-background transition-opacity hover:opacity-100 focus:ring-2 focus:ring-ring focus:ring-offset-2 focus:outline-hidden disabled:pointer-events-none data-[state=open]:bg-accent data-[state=open]:text-muted-foreground',
            /**
             * Rounded variant with white background and black X icon.
             * Ideal for use on dark or image backgrounds.
             */
            rounded:
                'absolute top-4 right-4 flex size-9 items-center justify-center gap-[0.20269rem] rounded-[var(--radius-btn-m)] bg-gray-100 text-gray-900 shadow-xs transition-colors hover:bg-gray-200 focus:ring-2 focus:ring-ring focus:ring-offset-2 focus:outline-hidden disabled:pointer-events-none',
            /** Smaller rounded variant for HDE modal (1.25rem) */
            roundedSmall:
                'absolute top-4 right-4 flex size-5 items-center justify-center gap-[0.20269rem] rounded-[var(--radius-btn-xs)] bg-gray-100 text-gray-900 shadow-xs transition-colors hover:bg-gray-200 focus:ring-2 focus:ring-ring focus:ring-offset-2 focus:outline-hidden disabled:pointer-events-none',
        },
    },
    defaultVariants: {
        variant: 'default',
    },
});

/** Available close button variant styles */
export type DialogCloseVariant =
    | 'default'
    | 'rounded'
    | 'roundedSmall'
    | null
    | undefined;

interface DialogCloseProps extends React.ComponentPropsWithoutRef<
    typeof DialogPrimitive.Close
> {
    /** The visual style variant of the close button */
    variant?: DialogCloseVariant;
}

export const DialogClose = ({
    className,
    variant,
    ...props
}: DialogCloseProps) => (
    <DialogPrimitive.Close
        className={cn(dialogCloseVariants({ variant, className }))}
        {...props}
    >
        <X
            className={
                variant === 'roundedSmall'
                    ? 'size-3.5'
                    : variant === 'rounded'
                      ? 'size-5'
                      : 'h-4 w-4'
            }
        />
        <span className="sr-only">Close</span>
    </DialogPrimitive.Close>
);
